import differenceInCalendarYears from "date-fns/differenceInCalendarYears";
import parseISO from "date-fns/parseISO";
import startOfToday from "date-fns/startOfToday";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { DASH, NOT_ACCESSIBLE } from "common/constants";
import { differenceInDaysOrNA } from "./date-utils";
import { parseHorseGenderFull, parseHorseGenderShort } from "./enum-parser";
import { getPersonFullName } from "./person";

export enum AlertEnum {
  na = "naAlert",
  non = "nonAlert",
  middle = "middleAlert",
  moderate = "moderateAlert",
  high = "highAlert"
}

export const getAlertStatus = (days: number, limit: number = 90): AlertEnum => {
  if (!Number.isInteger(days)) return AlertEnum.na;
  if (days > limit) return AlertEnum.high;
  if (days > 60) return AlertEnum.moderate;
  if (days > 30) return AlertEnum.middle;
  return AlertEnum.non;
};

export const toTitleCase = (str: string): string => {
  return str
    ?.toLowerCase()
    .trim()
    .split(/\s/)
    .map(word => word.charAt(0).toUpperCase() + word.substring(1))
    .join(" ");
};

export const formatBirthday = (
  date: string,
  isPluralizedString: boolean
): string => {
  const pluralize = (count, noun) =>
    `${count} ${noun}${count !== 1 ? "s" : ""}`;

  if (date) {
    const age =
      startOfToday().getUTCFullYear() - parseISO(date).getUTCFullYear();
    if (isPluralizedString) {
      return `${pluralize(age, "Year")} Old`;
    } else {
      return String(age);
    }
  } else {
    return "Unknown";
  }
};

export const getHorseAge = (
  horse: racehorse360.IHorse,
  isPluralizedString = false
): string => {
  return formatBirthday(horse?.birthday, isPluralizedString);
};

export const getWorkDaysOff = (horse: racehorse360.IHorse): number | string => {
  const raceOff = differenceInDaysOrNA(horse.lastRaceDate);
  const workoutOff = differenceInDaysOrNA(horse.lastWorkoutDate);
  const workOff = Math.min(
    Number.isInteger(workoutOff)
      ? Number(workoutOff)
      : Number.POSITIVE_INFINITY,
    Number.isInteger(raceOff) ? Number(raceOff) : Number.POSITIVE_INFINITY
  );
  return Number.isInteger(workOff) ? workOff : "N/A";
};

export const getHorseAgeSexColorValue = (
  horse: racehorse360.IHorse,
  format: string = "%c.%s.%a",
  notDeterminedValue: string = NOT_ACCESSIBLE
): string => {
  if (!horse) return notDeterminedValue;

  const age = horse.birthday
    ? String(differenceInCalendarYears(new Date(), new Date(horse.birthday)))
    : DASH;
  let color: string, shortColor: string, sex: string, shortSex: string;

  if (format.includes("%C")) {
    color = horse.colorDescription?.trim() || horse.color?.trim() || DASH;
  } else if (format.includes("%c")) {
    shortColor = horse.color?.trim() || DASH;
  }

  if (format.includes("%s")) {
    shortSex = parseHorseGenderShort(horse.gender);
  } else if (format.includes("%S")) {
    sex = parseHorseGenderFull(horse.gender);
  }

  return format
    .replace(/%c/g, shortColor)
    .replace(/%C/g, color)
    .replace(/%s/g, shortSex)
    .replace(/%S/g, sex)
    .replace(/%a/g, age);
};

export const getHorseOwnerOrBreederName = (
  horse: racehorse360.IHorse,
  notDeterminedValue: string = DASH
): string => {
  if (!horse) return notDeterminedValue;

  return (
    getPersonFullName(horse.owner, "%F %L") ||
    horse.breeder?.name ||
    notDeterminedValue
  );
};
